import type { ActiveEstimateDto, EstimateStatusResponse, GateResponse, QuotaErrorBody, StartEstimateRequest } from "@workcost/shared";
import { apiFetch } from "./api";
import { uploadPhotos } from "./upload";

/** เฉพาะ dev และตั้ง VITE_MOCK_API=1 เท่านั้น (นิพจน์นี้เป็น false ตายตัวใน production build) */
export function isMockApi(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1";
}

/** โหลด fixture เฉพาะตอนเปิดโหมด mock โค้ดส่วนนี้ถูกตัดออกจาก production build */
export async function loadMock(): Promise<typeof import("../mock/api") | null> {
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1") return import("../mock/api");
  return null;
}

export type EstimateErrorKind = "network" | "quota" | "concurrency" | "disabled" | "ai" | "unauthorized" | "gate_required" | "consent_required" | "other";

export class EstimateApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly reason?: QuotaErrorBody["reason"],
  ) {
    super(`${status} ${code}${reason ? `:${reason}` : ""}`);
    this.name = "EstimateApiError";
  }
}

/** จัดกลุ่ม error จาก API เพื่อเลือกข้อความและปุ่มที่ถูกต้อง */
export function classifyError(e: unknown): EstimateErrorKind {
  if (e instanceof EstimateApiError) {
    if (e.status === 401) return "unauthorized";
    if (e.status === 403 && e.code === "consent_required") return "consent_required";
    if (e.reason === "disabled") return "disabled";
    if (e.reason === "concurrency" || e.status === 429) return "concurrency";
    if (e.status === 402 || e.code === "quota") return "quota";
    if (e.code === "ai_unavailable" || e.code === "invalid_ai_output" || e.status === 502 || e.status === 503) return "ai";
    if (e.code === "gate_required" || e.code === "gate_mismatch") return "gate_required";
    return "other";
  }
  if (e instanceof TypeError) return "network";
  if (e instanceof Error && e.name === "ApiError") {
    const { status, code } = e as Error & { status?: number; code?: string };
    if (status === 403 && code === "consent_required") return "consent_required";
    if (status === 401) return "unauthorized";
    if (status === 429) return "concurrency";
    if (status === 402) return "quota";
    if (status === 502 || status === 503) return "ai";
  }
  return "other";
}

async function call<T>(path: string, init: { method: string; json?: unknown }): Promise<T> {
  const res = await apiFetch(path, init);
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; reason?: QuotaErrorBody["reason"] };
    throw new EstimateApiError(res.status, b.error ?? "error", b.reason);
  }
  return body as T;
}

/** อัปโหลดรูป (presign, PUT, verify) แล้วคืน object keys */
export async function uploadForGate(photos: readonly Blob[]): Promise<string[]> {
  const mock = await loadMock();
  if (mock) return mock.uploadPhotos(photos);
  return uploadPhotos(photos);
}

/** POST /estimates/gate */
export async function postGate(keys: string[]): Promise<GateResponse> {
  const mock = await loadMock();
  if (mock) return mock.postGate(keys);
  return call<GateResponse>("/estimates/gate", { method: "POST", json: { keys } });
}

/** POST /estimates (หัก/จองโควตาที่ขั้นนี้ ฝั่ง server เท่านั้น) */
export async function startEstimate(req: StartEstimateRequest): Promise<{ estimateId: string }> {
  const mock = await loadMock();
  if (mock) return mock.startEstimate(req);
  return call<{ estimateId: string }>("/estimates", { method: "POST", json: req });
}

/** GET /estimates/:id (poll ทุก 1.5 วินาทีที่หน้า S05) */
export async function getEstimate(id: string): Promise<EstimateStatusResponse> {
  const mock = await loadMock();
  if (mock) return mock.getEstimate(id);
  return call<EstimateStatusResponse>(`/estimates/${encodeURIComponent(id)}`, { method: "GET" });
}

/** GET /estimates/active : การประเมินที่ยังไม่เสร็จ (แสดงในหน้างานของฉัน) */
export async function listActiveEstimates(): Promise<ActiveEstimateDto[]> {
  const mock = await loadMock();
  if (mock) return [];
  return (await call<{ items: ActiveEstimateDto[] }>("/estimates/active", { method: "GET" })).items;
}

/** POST /estimates/:id/cancel (ไม่หักโควตา) */
export async function cancelEstimate(id: string): Promise<void> {
  const mock = await loadMock();
  if (mock) return mock.cancelEstimate(id);
  await call<{ ok: boolean }>(`/estimates/${encodeURIComponent(id)}/cancel`, { method: "POST" });
}

/** GET /categories ใช้ fixture เมื่อเปิดโหมด mock */
export async function mockCategories() {
  const mock = await loadMock();
  return mock ? mock.categories() : null;
}
